import { HttpError, useNotify, useTranslate } from 'ra-core';
import { type UseQueryResult } from '@tanstack/react-query';
import { type UnitPerformanceLlmInsightResponse } from '../../../../api/customEndpoints/fetchUnitPerformanceScores';
import { CardContent } from '../../../../components/ui/card';
import { Textarea } from '../../../../components/ui/textarea';
import { Button } from '../../../../components/ui/button';
import { Copy } from 'lucide-react';
import { Error } from '../../../../components/admin/error';
import { Loading } from '../../../../components/admin/loading';

export function AIGeneratedInsightsCard({
  unitLlmInsights,
  pendingLlmInsights,
  streamedLlmInsights,
}: {
  unitLlmInsights: UseQueryResult<UnitPerformanceLlmInsightResponse>;
  pendingLlmInsights: boolean;
  streamedLlmInsights: string | undefined;
}) {
  const notify = useNotify();
  const translate = useTranslate();

  const llmInsightsNotFound =
    unitLlmInsights.isError &&
    unitLlmInsights.error instanceof HttpError &&
    unitLlmInsights.error.status === 404;

  const displayedInsights =
    streamedLlmInsights ||
    (llmInsightsNotFound ? undefined : unitLlmInsights.data?.content);

  return (
    <CardContent>
      <h2 className="text-xl font-semibold">
        {translate('moonstar.unitPerformance.aiInsights.title')}
      </h2>
      <p className="mb-3 text-sm text-muted-foreground">
        {translate('moonstar.unitPerformance.aiInsights.description')}
      </p>
      {unitLlmInsights.isPending || pendingLlmInsights ? (
        <Loading />
      ) : unitLlmInsights.isError && !llmInsightsNotFound ? (
        <Error
          error={unitLlmInsights.error}
          resetErrorBoundary={() => void unitLlmInsights.refetch()}
        />
      ) : displayedInsights ? (
        <div className="relative">
          <Textarea
            value={displayedInsights}
            readOnly
            className="min-h-32 pr-12"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="absolute top-2 right-2"
            aria-label={translate('moonstar.unitPerformance.aiInsights.copy')}
            onClick={() => {
              navigator.clipboard
                .writeText(displayedInsights)
                .then(() => {
                  notify(
                    translate('moonstar.unitPerformance.aiInsights.copied'),
                    { type: 'success' }
                  );
                })
                .catch(() => {
                  notify(
                    translate('moonstar.unitPerformance.aiInsights.copyFailed'),
                    { type: 'error' }
                  );
                });
            }}
          >
            <Copy />
          </Button>
        </div>
      ) : (
        <p>{translate('moonstar.unitPerformance.aiInsights.empty')}</p>
      )}
    </CardContent>
  );
}
